import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BUSINESS, WHATSAPP, ROUTES } from '../../data/pmContent';
import { IconPin, IconPhone, IconUser, IconChevronRight, IconChevronDown } from './PmIcons';

/**
 * Header global: topbar + header sticky + menú móvil + barra CTA fija (solo en la home).
 *
 * Adaptado del entregable para usarse en TODAS las rutas:
 *  - Los links de sección son rutas absolutas ("/#ofertas") para funcionar desde cualquier página.
 *  - Muestra el menú de usuario cuando hay sesión iniciada.
 *  - Cada bloque va en su propio `.pm-scope` para que el resto del sitio (Bootstrap) no se entere.
 *
 * NOTA: la marca visible es "PM Beauty Studio". La palabra "PaoMakeup" ya no
 * aparece ni en el cintillo ni en el header.
 */

const NAV_LINKS = [
  [ROUTES.servicios, 'Servicios'],
  [ROUTES.precios, 'Precios'],
  ['/#ofertas', 'Ofertas'],
  [ROUTES.nosotros, 'Nosotros'],
];

const ACCOUNT_LINKS = [
  ['/mis-citas', 'Mis citas'],
  ['/fidelidad', 'Fidelidad'],
  ['/mi-cuenta', 'Mi cuenta'],
];

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const firstName = (user?.name || 'Mi cuenta').split(' ')[0];

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-menu__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-menu__avatar" aria-hidden="true">{firstName.charAt(0).toUpperCase()}</span>
        <span className="user-menu__name">{firstName}</span>
        <IconChevronDown width="14" height="14" />
      </button>
      {open && (
        <div className="user-menu__panel" role="menu">
          {ACCOUNT_LINKS.map(([to, label]) => (
            <Link key={to} role="menuitem" to={to} onClick={() => setOpen(false)}>{label}</Link>
          ))}
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onLogout(); }}>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

export default function PmHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [barVisible, setBarVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 12);
        setBarVisible(y > 420);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Cierra el menú móvil al cambiar de ruta
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

  const close = useCallback(() => setMenuOpen(false), []);

  const handleLogout = useCallback(async () => {
    close();
    await logout();
    navigate('/');
  }, [close, logout, navigate]);

  return (
    <>
      <div className="pm-scope">
        <div className="topbar">
          <div className="container topbar__inner">
            <a className="topbar__hide-sm" href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer">
              <IconPin strokeWidth="2" /> Los Prados, Santo Domingo
            </a>
            <span className="topbar__sep topbar__hide-sm" aria-hidden="true">·</span>
            <a href={`tel:${BUSINESS.phone1Tel}`}><IconPhone strokeWidth="2" /> {BUSINESS.phone1}</a>
            <span className="topbar__sep" aria-hidden="true">·</span>
            <Link to="/#ofertas">Ver ofertas del mes</Link>
          </div>
        </div>
      </div>

      <div className="pm-scope pm-scope--sticky">
        <header className={`header${scrolled ? ' is-scrolled' : ''}`}>
          <div className="container container--wide header__inner">
            <Link className="brand" to={ROUTES.home} aria-label={`${BUSINESS.name}, inicio`}>
              <img
                className="brand__mark"
                src={BUSINESS.logo}
                srcSet={`${BUSINESS.logo} 1x, ${BUSINESS.logo2x} 2x`}
                alt=""
                width="52"
                height="52"
              />
              <span className="brand__text">
                <span className="brand__name">{BUSINESS.name}</span>
                <span className="brand__tag">Los Prados, Santo Domingo</span>
              </span>
            </Link>

            <nav className="nav" aria-label="Navegación principal">
              {NAV_LINKS.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
            </nav>

            <div className="header__actions">
              {isAuthenticated ? (
                <UserMenu user={user} onLogout={handleLogout} />
              ) : (
                <Link className="header__login" to={ROUTES.login} aria-label="Iniciar sesión"><IconUser /></Link>
              )}
              <a className="btn btn--secondary btn--sm" href={WHATSAPP} target="_blank" rel="noopener noreferrer">WhatsApp</a>
              <Link className="btn btn--primary" to={ROUTES.agendar}>Agendar cita</Link>
              <button
                className="burger"
                type="button"
                aria-expanded={menuOpen}
                aria-controls="pm-mobile-nav"
                aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span /><span /><span />
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="pm-scope pm-scope--overlay">
        <div className={`mobile-nav${menuOpen ? ' is-open' : ''}`} id="pm-mobile-nav">
          {NAV_LINKS.map(([to, label]) => (
            <Link key={to} className="mobile-nav__link" to={to} onClick={close}>
              {label} <IconChevronRight />
            </Link>
          ))}
          {isAuthenticated ? (
            <>
              <div className="mobile-nav__group">Hola, {(user?.name || '').split(' ')[0] || 'clienta'}</div>
              {ACCOUNT_LINKS.map(([to, label]) => (
                <Link key={to} className="mobile-nav__link" to={to} onClick={close}>
                  {label} <IconChevronRight />
                </Link>
              ))}
              <button type="button" className="mobile-nav__link mobile-nav__link--btn" onClick={handleLogout}>
                Cerrar sesión <IconChevronRight />
              </button>
            </>
          ) : (
            <Link className="mobile-nav__link" to={ROUTES.login} onClick={close}>
              Iniciar sesión <IconChevronRight />
            </Link>
          )}
          <div className="mobile-nav__cta">
            <Link className="btn btn--primary btn--lg btn--block" to={ROUTES.agendar} onClick={close}>Agendar cita</Link>
            <a className="btn btn--wa btn--block" href={WHATSAPP} target="_blank" rel="noopener noreferrer">Escribir por WhatsApp</a>
          </div>
        </div>

        {isHome && (
          <div className={`mobile-bar${barVisible ? ' is-visible' : ''}`}>
            <a className="btn btn--secondary" href={WHATSAPP} target="_blank" rel="noopener noreferrer">WhatsApp</a>
            <Link className="btn btn--primary" to={ROUTES.agendar}>Agendar cita</Link>
          </div>
        )}
      </div>
    </>
  );
}
